import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CustomTagsField,
  Details,
  type DetailsProps,
  Divider,
  GenericField,
  getFormDiff,
  ImgElement,
  InfoPanel,
  InfoTooltip,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, type FormikValues } from 'formik';
import ipaddr from 'ipaddr.js';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { EnvironmentServiceState } from '../../../../generated/id-service-env-administration';
import {
  type MutationUpdateDomainEnvironmentMappingArgs,
  type PortalServiceConfigurationQuery,
  PortalUrlType,
  useCreateIpWhitelistPatternMutation,
  useDeleteIpWhitelistPatternMutation,
  usePortalServiceConfigurationQuery,
  useUpdateDomainEnvironmentMappingMutation,
} from '../../../../generated/portal-service';
import {
  formatEnumValueToTitleCase,
  getManagedServiceData,
  getServiceStateIcon,
} from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './PortalServiceConfiguration.module.scss';

type FormData = Nullable<
  MutationUpdateDomainEnvironmentMappingArgs['input']['patch']
> & {
  ipWhitelistPatterns?: string[] | null;
};

const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d{2}|[1-9]\\d|\\d)';
const IPV4_CIDR_RE = new RegExp(
  `^(${IPV4_OCTET}\\.){3}${IPV4_OCTET}\\/([0-9]|[12]\\d|3[0-2])$`,
);

const IPV6_GROUP = '[0-9a-fA-F]{1,4}';
const IPV6_CIDR_RE = new RegExp(
  `^((${IPV6_GROUP}:){7}${IPV6_GROUP}` + // full form: 8 groups
    `|((${IPV6_GROUP}:){0,6}${IPV6_GROUP})?::((${IPV6_GROUP}:){0,6}${IPV6_GROUP})?` + // compressed: at most one ::
    `)\\/(12[0-8]|1[01]\\d|\\d{1,2})$`,
);

function isIPv4CIDR(value: string): boolean {
  return IPV4_CIDR_RE.test(value);
}

function isIPv6CIDR(value: string): boolean {
  return value.includes(':') && IPV6_CIDR_RE.test(value);
}

// Returns the corrected network address if host bits are set, null if already clean.
function cidrNetworkSuggestion(cidr: string): string | null {
  try {
    const [addr, bits] = ipaddr.parseCIDR(cidr);
    const addrBytes = addr.toByteArray();
    const networkBytes = addrBytes.map((byte, i) => {
      const bitStart = i * 8;
      if (bitStart + 8 <= bits) {
        return byte;
      }
      if (bitStart >= bits) {
        return 0;
      }
      return byte & ((0xff << (8 - (bits - bitStart))) & 0xff);
    });
    if (addrBytes.every((b, i) => b === networkBytes[i])) {
      return null;
    }
    return `${ipaddr.fromByteArray(networkBytes)}/${bits}`;
  } catch {
    return null;
  }
}

const portalServiceConfigSchema = Yup.object<ObjectSchemaDefinition<FormData>>({
  portalSubDomain: Yup.string()
    .required('Subdomain is a required field')
    .min(3, 'Subdomain must be at least 3 characters')
    .max(63, 'Subdomain must be at most 63 characters')
    .matches(
      /^(([a-z0-9]|[a-z0-9][a-z0-9\\-]*[a-z0-9]))$/,
      "Invalid subdomain format. Subdomain cannot contain any uppercase, or special characters except '-'",
    ),
  ipWhitelistPatterns: Yup.array()
    .min(
      1,
      'At least one IP whitelist pattern is required. Use 0.0.0.0/0 (all IPv4) or ::/0 (all IPv6) to allow all.',
    )
    .of(
      Yup.string().test(
        'valid-cidr',
        'Each entry must be a valid CIDR block — IPv4 (e.g. 192.168.0.0/24) or IPv6 (e.g. 2001:db8::/32).',
        function (value) {
          if (!value) {
            return false;
          }
          if (!isIPv4CIDR(value) && !isIPv6CIDR(value)) {
            return false;
          }
          const suggestion = cidrNetworkSuggestion(value);
          if (suggestion) {
            return this.createError({
              message: `Host bits must be zero — did you mean ${suggestion}?`,
            });
          }
          return true;
        },
      ),
    )
    .test('wildcard-exclusive', '', function (value) {
      if (!value || value.length <= 1) {
        return true;
      }
      const trimmed = value.map((v) => v?.trim() ?? '');
      const hasIPv4Wildcard = trimmed.some((v) => v === '0.0.0.0/0');
      const hasIPv6Wildcard = trimmed.some((v) => v === '::/0');
      const otherIPv4 = trimmed.filter(
        (v) => v && isIPv4CIDR(v) && v !== '0.0.0.0/0',
      );
      const otherIPv6 = trimmed.filter(
        (v) => v && isIPv6CIDR(v) && v !== '::/0',
      );
      if (hasIPv4Wildcard && otherIPv4.length > 0) {
        return this.createError({
          message:
            '0.0.0.0/0 already covers all IPv4 addresses. Remove the specific IPv4 ranges or remove 0.0.0.0/0.',
        });
      }
      if (hasIPv6Wildcard && otherIPv6.length > 0) {
        return this.createError({
          message:
            '::/0 already covers all IPv6 addresses. Remove the specific IPv6 ranges or remove ::/0.',
        });
      }
      return true;
    }),
});

export const PortalServiceConfiguration: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();

  const [serviceState, setServiceState] = useState<
    EnvironmentServiceState | undefined
  >();
  const [serviceStateError, setServiceStateError] = useState<
    string | undefined
  >();

  useEffect(() => {
    setServiceState(undefined);
    setServiceStateError(undefined);
    (async () => {
      try {
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.PORTAL_SERVICE,
        );
        setServiceState(serviceData?.state);
      } catch (err) {
        setServiceStateError(
          err instanceof Error ? err.message : 'Failed to load service state.',
        );
      }
    })();
  }, [envId]);

  const envApolloClient = useMemo(
    () =>
      environmentApolloClient(
        envId,
        new URL('/graphql', config.portalServiceAdminBaseUrl).toString(),
      ),
    [envId],
  );

  const { data, loading, error, refetch } = usePortalServiceConfigurationQuery({
    client: envApolloClient,
    fetchPolicy: 'no-cache',
    skip: serviceState !== EnvironmentServiceState.Enabled,
  });

  const [updateDomainEnvironmentMapping] =
    useUpdateDomainEnvironmentMappingMutation({ client: envApolloClient });

  const [createIpWhitelistPattern] = useCreateIpWhitelistPatternMutation({
    client: envApolloClient,
  });

  const [deleteIpWhitelistPattern] = useDeleteIpWhitelistPatternMutation({
    client: envApolloClient,
  });

  const domainMapping = data?.domainEnvironmentMappings?.nodes[0];

  const initialPatterns = useMemo(
    () => data?.ipWhitelistPatterns?.edges?.map((e) => e.node) ?? [],
    [data],
  );

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<void> => {
      if (initialData.loading || !domainMapping) {
        return;
      }

      const patch = createUpdateDto(formData, initialData.data);
      if (Object.keys(patch).length > 0) {
        await updateDomainEnvironmentMapping({
          variables: {
            input: {
              id: domainMapping.id,
              patch,
            },
          },
        });
      }

      const newPatterns = Array.from(
        new Set(
          (formData.ipWhitelistPatterns ?? []).map((p) =>
            p.trim().toLowerCase(),
          ),
        ),
      );

      const toCreate = newPatterns.filter(
        (p) =>
          !initialPatterns.some((ip) => ip.pattern.trim().toLowerCase() === p),
      );
      const toDelete = initialPatterns.filter(
        (ip) => !newPatterns.includes(ip.pattern.trim().toLowerCase()),
      );

      await Promise.all([
        ...toCreate.map((pattern) =>
          createIpWhitelistPattern({
            variables: { input: { ipWhitelistPattern: { pattern } } },
          }),
        ),
        ...toDelete.map((ip) =>
          deleteIpWhitelistPattern({
            variables: { input: { id: ip.id } },
          }),
        ),
      ]);

      await refetch();
    },
    [
      domainMapping,
      initialPatterns,
      updateDomainEnvironmentMapping,
      createIpWhitelistPattern,
      deleteIpWhitelistPattern,
      refetch,
    ],
  );

  return (
    <Details
      defaultTitle="Management System Service"
      subtitle="Configuration"
      validationSchema={portalServiceConfigSchema}
      initialData={{
        data: domainMapping
          ? {
              portalSubDomain: domainMapping.portalSubDomain,
              ipWhitelistPatterns: initialPatterns.map((p) => p.pattern),
            }
          : {},
        loading: (serviceState === undefined && !serviceStateError) || loading,
        error: error?.message ?? serviceStateError,
      }}
      saveData={onSubmit}
      infoPanel={<Panel domainMapping={domainMapping} />}
    >
      <Form serviceState={serviceState} />
    </Details>
  );
};

const Form: React.FC<{
  serviceState: EnvironmentServiceState | undefined;
}> = ({ serviceState }) => {
  return (
    <>
      <GenericField name="serviceState" label="Status">
        <div className={classes.status_container}>
          <ImgElement
            src={getServiceStateIcon(
              serviceState,
              ManagedServiceId.PORTAL_SERVICE,
            )}
            style={{ width: '20px', height: '20px' }}
            decorative={true}
          />
          <div className={classes.status}>
            {formatEnumValueToTitleCase(serviceState)}
          </div>
        </div>
      </GenericField>

      <Divider />

      <Field
        name="portalSubDomain"
        label="Subdomain"
        as={SingleLineTextField}
        disabled={serviceState !== EnvironmentServiceState.Enabled}
      />
      <Field
        name="ipWhitelistPatterns"
        label="IP Whitelist Patterns"
        tooltipContent="Enter CIDR ranges — IPv4 (e.g. 192.168.0.0/24) or IPv6 (e.g. 2001:db8::/32). Use 0.0.0.0/0 to allow all IPv4 and ::/0 to allow all IPv6."
        as={CustomTagsField}
        disabled={serviceState !== EnvironmentServiceState.Enabled}
      />
    </>
  );
};

function createUpdateDto(
  currentValues: FormikValues,
  initialValues: FormikValues | undefined | null,
): FormikValues {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData['ipWhitelistPatterns'];
  return updateData;
}

type DomainMappingNode = NonNullable<
  NonNullable<
    PortalServiceConfigurationQuery['domainEnvironmentMappings']
  >['nodes'][0]
>;

const Panel: React.FC<{
  domainMapping: DomainMappingNode | undefined;
}> = ({ domainMapping }) => {
  const portalUrls = domainMapping?.portalUrls ?? [];
  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Management System URLs">
          {portalUrls.map((portalUrl, index) => (
            <div key={index} className={classes.urlDiv}>
              <a href={portalUrl.url} target="_blank" rel="noopener noreferrer">
                {portalUrl.url}
              </a>
              {portalUrl.urlType === PortalUrlType.Default && (
                <InfoTooltip className={classes.urlInfo}>
                  Derived from the Subdomain field below. Change the subdomain
                  to update this URL.
                </InfoTooltip>
              )}
            </div>
          ))}
        </Paragraph>
        <Paragraph title="Last Modified">
          {domainMapping
            ? new Date(domainMapping.updatedDate).toLocaleString() +
              ' by ' +
              domainMapping.updatedUser
            : ''}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{domainMapping?.tenantId}</Paragraph>
        <Paragraph title="Environment ID">
          {domainMapping?.environmentId}
        </Paragraph>
      </Section>
    </InfoPanel>
  );
};

export const PortalServiceConfigurationCrumb: BreadcrumbResolver = () =>
  'Management System Service';
